import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FileText, Eye, Check, X, Upload, Download, Search, Trash2, Link2, Copy, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Use native XMLHttpRequest to bypass rrweb-recorder fetch wrapper
const nativeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    
    // Set headers
    if (options.headers) {
      Object.keys(options.headers).forEach(key => {
        xhr.setRequestHeader(key, options.headers[key]);
      });
    }
    
    xhr.onload = function() {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ ok: true, data, status: xhr.status });
        } catch (e) {
          resolve({ ok: true, data: xhr.responseText, status: xhr.status });
        }
      } else {
        try {
          const error = JSON.parse(xhr.responseText);
          reject({ ok: false, error, status: xhr.status });
        } catch (e) {
          reject({ ok: false, error: { detail: xhr.statusText }, status: xhr.status });
        }
      }
    };
    
    xhr.onerror = function() {
      reject({ ok: false, error: { detail: 'Network error' }, status: 0 });
    };
    
    // Send request
    if (options.body) {
      xhr.send(options.body);
    } else {
      xhr.send();
    }
  });
};

export default function ProposalsManager() {
  const { admin, getAuthHeader } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [manualEmail, setManualEmail] = useState('');
  const [approving, setApproving] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  
  // Filter states
  const [leadIdFilter, setLeadIdFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchProposals();
  }, []);

  const fetchProposals = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/proposals`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setProposals(data);
      }
    } catch (error) {
      console.error('Error fetching proposals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyApprovalLink = (proposal) => {
    const link = `${window.location.origin}/public/proposal/${proposal.approval_token}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopiedId(proposal.id);
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedId(proposal.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };


  const handleScreenshotUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/proposals/${selectedProposal.id}/upload-screenshot`,
        {
          method: 'POST',
          headers: getAuthHeader(),
          body: formData
        }
      );

      if (response.ok) {
        const data = await response.json();
        return data.screenshot_url;
      }
    } catch (error) {
      console.error('Error uploading screenshot:', error);
    }
    return null;
  };

  const handleApprove = async () => {
    if (!screenshot && admin?.role !== 'admin' && admin?.role !== 'super_admin') {
      alert('Please upload a screenshot of the email');
      return;
    }

    setApproving(true);
    try {
      let screenshotUrl = null;
      if (screenshot) {
        screenshotUrl = await handleScreenshotUpload(screenshot);
        if (!screenshotUrl) {
          alert('Failed to upload screenshot');
          return;
        }
      }

      const approvalMethod = (admin?.role === 'admin' || admin?.role === 'super_admin') 
        ? 'super_admin_override' 
        : 'email_validation';

      const response = await nativeFetch(
        `${BACKEND_URL}/api/proposals/${selectedProposal.id}/approve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
          },
          body: JSON.stringify({
            approved: true,
            approval_method: approvalMethod,
            screenshot_file: screenshotUrl,
            manual_email: manualEmail || null
          })
        }
      );

      if (response.ok) {
        alert('Proposal approved successfully!');
        setShowApprovalModal(false);
        setSelectedProposal(null);
        setScreenshot(null);
        setManualEmail('');
        fetchProposals();
      }
    } catch (error) {
      console.error('Error approving proposal:', error);
      alert(error.error?.detail || 'Failed to approve proposal');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    setRejecting(true);
    try {
      const response = await nativeFetch(
        `${BACKEND_URL}/api/proposals/${selectedProposal.id}/reject`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
          },
          body: JSON.stringify({
            rejection_reason: rejectionReason
          })
        }
      );

      if (response.ok) {
        alert('Proposal rejected successfully');
        setShowRejectModal(false);
        setSelectedProposal(null);
        setRejectionReason('');
        fetchProposals();
      }
    } catch (error) {
      console.error('Error rejecting proposal:', error);
      alert('Failed to reject proposal');
    } finally {
      setRejecting(false);
    }
  };

  const handleDeleteProposal = async (proposalId) => {
    const confirmed = await window.confirm('Are you sure you want to delete this proposal? This action cannot be undone.');
    if (!confirmed) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/proposals/${proposalId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });

      if (response.ok) {
        alert('Proposal deleted successfully');
        fetchProposals(); // Refresh the list
      } else {
        const error = await response.json();
        alert(`Failed to delete proposal: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting proposal:', error);
      alert('Failed to delete proposal');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter proposals based on filters
  const filteredProposals = proposals.filter(proposal => {
    if (leadIdFilter && !proposal.lead_id?.toLowerCase().includes(leadIdFilter.toLowerCase())) {
      return false;
    }
    if (statusFilter && proposal.status !== statusFilter) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading proposals...</div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Proposals Management</h1>
          <p className="text-gray-600">View and manage all proposals</p>
        </div>

        {/* Filters */}
        <div className="mb-6 bg-white rounded-lg shadow-md p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search by Lead ID
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Enter Lead ID..."
                  value={leadIdFilter}
                  onChange={(e) => setLeadIdFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setLeadIdFilter('');
                  setStatusFilter('');
                }}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {filteredProposals.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <FileText size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">
              {proposals.length === 0 ? 'No proposals found' : 'No proposals match your filters'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proposal #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destination</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issue Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredProposals.map(proposal => (
                    <tr key={proposal.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-purple-600">
                        {proposal.lead_id}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {proposal.proposal_number}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{proposal.lead_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{proposal.destination_country || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-semibold">
                        ₹{proposal.total_amount ? proposal.total_amount.toFixed(2) : '0.00'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(proposal.status)}`}>
                          {proposal.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(proposal.issue_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={async () => {
                              try {
                                const response = await fetch(
                                  `${BACKEND_URL}/api/proposals/${proposal.id}/download-pdf`,
                                  { headers: getAuthHeader() }
                                );
                                if (response.ok) {
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.setAttribute('download', `proposal_${proposal.proposal_number.replace(/\//g, '_')}.pdf`);
                                  document.body.appendChild(link);
                                  link.click();
                                  link.remove();
                                } else {
                                  alert('Failed to download PDF');
                                }
                              } catch (error) {
                                console.error('Error downloading PDF:', error);
                                alert('Failed to download PDF');
                              }
                            }}
                            className="text-purple-600 hover:text-purple-800"
                            title="Download PDF"
                          >
                            <Download size={18} />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedProposal(proposal);
                              setShowApprovalModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800"
                            title="View Details"
                          >
                            <Eye size={18} />
                          </button>
                          {proposal.status !== 'approved' && proposal.approval_token && (
                            <button
                              onClick={() => handleCopyApprovalLink(proposal)}
                              className={`${copiedId === proposal.id ? 'text-green-600' : 'text-indigo-600 hover:text-indigo-800'}`}
                              title={copiedId === proposal.id ? 'Link Copied!' : 'Copy Approval Link'}
                              data-testid={`copy-link-${proposal.id}`}
                            >
                              {copiedId === proposal.id ? <CheckCircle size={18} /> : <Link2 size={18} />}
                            </button>
                          )}
                          {proposal.status === 'draft' && (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedProposal(proposal);
                                  setShowApprovalModal(true);
                                }}
                                className="text-green-600 hover:text-green-800"
                                title="Approve"
                              >
                                <Check size={18} />
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedProposal(proposal);
                                  setShowRejectModal(true);
                                }}
                                className="text-red-600 hover:text-red-800"
                                title="Reject"
                              >
                                <X size={18} />
                              </button>
                            </>
                          )}
                          {(admin?.role === 'super_admin' || admin?.roles?.includes('super_admin') || 
                            admin?.role === 'admin' || admin?.roles?.includes('admin')) && (
                            <button
                              onClick={() => handleDeleteProposal(proposal.id)}
                              className="text-red-600 hover:text-red-800"
                              title="Delete Proposal"
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Approval Modal */}
      {showApprovalModal && selectedProposal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg w-full max-w-2xl">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">
                Proposal {selectedProposal.proposal_number}
              </h2>
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setSelectedProposal(null);
                  setScreenshot(null);
                  setManualEmail('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2">Proposal Details</h3>
                <div className="space-y-1 text-sm">
                  <p><span className="font-medium">Lead ID:</span> {selectedProposal.lead_id}</p>
                  <p><span className="font-medium">Lead:</span> {selectedProposal.lead_name}</p>
                  <p><span className="font-medium">Email:</span> {selectedProposal.lead_email}</p>
                  <p><span className="font-medium">Amount:</span> ₹{selectedProposal.total_amount ? selectedProposal.total_amount.toFixed(2) : '0.00'}</p>
                  <p><span className="font-medium">Status:</span> {selectedProposal.status}</p>
                </div>
              </div>

              {selectedProposal.status !== 'approved' && selectedProposal.approval_token && (
                <div className="bg-indigo-50 border border-indigo-200 p-4 rounded-lg" data-testid="proposal-approval-link-section">
                  <h3 className="font-semibold text-indigo-800 mb-2 flex items-center gap-2">
                    <Link2 size={16} /> Customer Approval Link
                  </h3>
                  <p className="text-sm text-indigo-700 mb-3">
                    Share this link with the customer via WhatsApp, SMS, or any other channel so they can approve the proposal online.
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={`${window.location.origin}/public/proposal/${selectedProposal.approval_token}`}
                      className="flex-1 px-3 py-2 border border-indigo-300 rounded-lg bg-white text-sm text-gray-700"
                      data-testid="proposal-approval-link-input"
                    />
                    <button
                      onClick={() => handleCopyApprovalLink(selectedProposal)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1 ${
                        copiedId === selectedProposal.id
                          ? 'bg-green-600 text-white'
                          : 'bg-indigo-600 text-white hover:bg-indigo-700'
                      }`}
                      data-testid="copy-proposal-approval-link-btn"
                    >
                      {copiedId === selectedProposal.id ? (
                        <><CheckCircle size={14} /> Copied!</>
                      ) : (
                        <><Copy size={14} /> Copy Link</>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {(admin?.role !== 'admin' && admin?.role !== 'super_admin') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload Email Screenshot <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setScreenshot(e.target.files[0])}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Upload a screenshot showing the email sent to the client
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Manual Email Verification (optional)
                    </label>
                    <input
                      type="email"
                      placeholder="Enter email from screenshot if AI fails"
                      value={manualEmail}
                      onChange={(e) => setManualEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </>
              )}

              {(admin?.role === 'admin' || admin?.role === 'super_admin') && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Admin Override:</strong> As an admin, you can approve this proposal without uploading a screenshot.
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleApprove}
                  disabled={approving}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 text-white py-2 px-4 rounded-lg hover:from-purple-700 hover:to-purple-800 disabled:opacity-50"
                >
                  {approving ? 'Approving...' : 'Approve Proposal'}
                </button>
                <button
                  onClick={() => {
                    setShowApprovalModal(false);
                    setSelectedProposal(null);
                    setScreenshot(null);
                    setManualEmail('');
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectModal && selectedProposal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">Reject Proposal</h2>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedProposal(null);
                  setRejectionReason('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Rejecting proposal for <strong>{selectedProposal.lead_name}</strong>
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="Please provide a reason for rejection..."
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleReject}
                  disabled={rejecting || !rejectionReason.trim()}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {rejecting ? 'Rejecting...' : 'Confirm Rejection'}
                </button>
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedProposal(null);
                    setRejectionReason('');
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
